import { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import LayoutWeb from "../LayoutWeb/layoutWeb";
import ListingFaqForm from "./ListingFaqForm";
import { getAllZones } from "../../api/zones.api";
import {
  createListingFaq,
  deleteListingFaq,
  getListingFaqs,
  getPagesWithListingFaqs,
  reorderListingFaqs,
  updateListingFaq,
} from "../../api/listingFaqs.api";
import {
  LISTING_FAQ_DEPARTMENTS,
  LISTING_FAQ_OPERATIONS,
} from "../../constants/listing-faqs-constants";
import {
  buildListingPath,
  buildListingUrl,
  describePage,
  getDepartment,
  isCompletePage,
  moveItem,
  pageForDepartment,
  zoneOptionsForPage,
} from "../../functions/listingFaqs/listing-faqs-functions";
import "./ListingFaqs.scss";

const NEW_FAQ = "new";

const errorMessage = (error) =>
  error?.response?.data?.message || "No se ha podido completar la acción.";

// FAQs de los listados de la web (por departamento, ciudad, barrio y
// operación). Las mantiene el equipo de SEO; la web las muestra debajo del
// listado de esa misma URL.
const ListingFaqs = () => {
  const [zones, setZones] = useState([]);
  const [page, setPage] = useState(() => pageForDepartment("residencial"));
  const [faqs, setFaqs] = useState([]);
  const [pagesWithFaqs, setPagesWithFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // null = sin formulario; NEW_FAQ = alta; id = edición
  const [editing, setEditing] = useState(null);

  const department = getDepartment(page.department);
  const hasCities = department.cities.length > 0;

  const zoneOptions = useMemo(
    () => zoneOptionsForPage(zones, page),
    [zones, page],
  );
  const zoneName = (slug) => zones.find((zone) => zone.slug === slug)?.name;

  const refreshPagesWithFaqs = useCallback(async () => {
    try {
      setPagesWithFaqs(await getPagesWithListingFaqs());
    } catch (err) {
      console.error("Error cargando el resumen de FAQs:", err);
    }
  }, []);

  useEffect(() => {
    getAllZones()
      .then((data) => setZones(data?.zones ?? []))
      .catch((err) => console.error("Error cargando las zonas:", err));
    refreshPagesWithFaqs();
  }, [refreshPagesWithFaqs]);

  // FAQs de la página seleccionada
  useEffect(() => {
    if (!isCompletePage(page)) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setEditing(null);

    getListingFaqs(page)
      .then((data) => !cancelled && setFaqs(data))
      .catch((err) => !cancelled && setError(errorMessage(err)))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [page]);

  const changePage = (changes) => setPage((current) => ({ ...current, ...changes }));

  // Ejecuta una escritura y vuelve a sincronizar la lista y el resumen
  const runWrite = async (write) => {
    setIsSaving(true);
    setError(null);
    try {
      await write();
      setEditing(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setFaqs(await getListingFaqs(page).catch(() => faqs));
      refreshPagesWithFaqs();
      setIsSaving(false);
    }
  };

  const handleSubmit = (values) =>
    runWrite(() =>
      editing === NEW_FAQ
        ? createListingFaq(page, values)
        : updateListingFaq(editing, values),
    );

  const handleDelete = (faq) => {
    if (window.confirm(`¿Eliminar la pregunta "${faq.question}"?`)) {
      runWrite(() => deleteListingFaq(faq._id));
    }
  };

  const handleMove = (index, direction) => {
    const reordered = moveItem(faqs, index, direction);
    if (reordered === faqs) return;
    setFaqs(reordered);
    runWrite(() => reorderListingFaqs(page, reordered.map((faq) => faq._id)));
  };

  return (
    <LayoutWeb subTitle="FAQs de los listados">
      <div className="ListingFaqs">
        <header className="ListingFaqs__header">
          <h1>FAQs de los listados</h1>
          <p>
            Preguntas frecuentes que aparecen debajo de cada listado de la web,
            por barrio y operación. "Venta y alquiler" es la página que se ve
            con las dos operaciones marcadas a la vez (o ninguna). Si una página
            no tiene FAQs, no se muestra el bloque.
          </p>
        </header>

        <section className="ListingFaqs__selector">
          <label>
            <span>Departamento</span>
            <Dropdown
              value={page.department}
              options={LISTING_FAQ_DEPARTMENTS}
              onChange={(event) =>
                setPage(pageForDepartment(event.value, page.operation))
              }
            />
          </label>

          {/* Ciudad y barrio siempre visibles (desactivados en "Otros activos
              & zonas") para que la fila no cambie de tamaño */}
          <label>
            <span>Ciudad</span>
            <Dropdown
              value={page.city}
              options={department.cities}
              onChange={(event) => changePage({ city: event.value, zone: null })}
              disabled={!hasCities}
              placeholder={hasCities ? "Elige una ciudad" : "No aplica"}
            />
          </label>

          <label className="ListingFaqs__selector__zone">
            <span>Barrio</span>
            <Dropdown
              value={page.zone}
              options={zoneOptions}
              onChange={(event) => changePage({ zone: event.value ?? null })}
              disabled={!hasCities}
              placeholder={hasCities ? "Toda la ciudad" : "No aplica"}
              filter
              // El texto del buscador no se queda puesto para otra ciudad
              resetFilterOnHide
              showClear={hasCities}
              emptyMessage="Sin barrios"
              emptyFilterMessage="Ningún barrio coincide"
            />
          </label>

          <label>
            <span>Operación</span>
            <Dropdown
              value={page.operation}
              options={LISTING_FAQ_OPERATIONS}
              onChange={(event) => changePage({ operation: event.value })}
            />
          </label>
        </section>

        <section className="ListingFaqs__page">
          <div className="ListingFaqs__page__title">
            <h2>{describePage(page, zoneName(page.zone))}</h2>
            <a
              href={buildListingUrl(page)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver en la web: {buildListingPath(page)}
            </a>
          </div>
          {editing === null && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setEditing(NEW_FAQ)}
            >
              + Nueva pregunta
            </button>
          )}
        </section>

        {error && <p className="ListingFaqs__error">{error}</p>}

        {editing === NEW_FAQ && (
          <ListingFaqForm
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onCancel={() => setEditing(null)}
          />
        )}

        {loading ? (
          <p className="ListingFaqs__empty">Cargando preguntas...</p>
        ) : faqs.length === 0 && editing !== NEW_FAQ ? (
          <p className="ListingFaqs__empty">
            Esta página todavía no tiene preguntas.
          </p>
        ) : (
          <ol className="ListingFaqs__list">
            {faqs.map((faq, index) =>
              editing === faq._id ? (
                <li key={faq._id}>
                  <ListingFaqForm
                    initialValues={faq}
                    isSaving={isSaving}
                    onSubmit={handleSubmit}
                    onCancel={() => setEditing(null)}
                  />
                </li>
              ) : (
                <li key={faq._id} className="ListingFaqs__item">
                  <div className="ListingFaqs__item__text">
                    <strong>{faq.question}</strong>
                    <p>{faq.answer}</p>
                  </div>
                  <div className="ListingFaqs__item__actions">
                    <button
                      type="button"
                      title="Subir"
                      aria-label="Subir"
                      disabled={isSaving || index === 0}
                      onClick={() => handleMove(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      title="Bajar"
                      aria-label="Bajar"
                      disabled={isSaving || index === faqs.length - 1}
                      onClick={() => handleMove(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn-edit"
                      disabled={isSaving}
                      onClick={() => setEditing(faq._id)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="btn-delete"
                      disabled={isSaving}
                      onClick={() => handleDelete(faq)}
                    >
                      Eliminar
                    </button>
                  </div>
                </li>
              ),
            )}
          </ol>
        )}

        <section className="ListingFaqs__summary">
          <h2>Páginas con FAQs</h2>
          {pagesWithFaqs.length === 0 ? (
            <p className="ListingFaqs__empty">Ninguna todavía.</p>
          ) : (
            <ul>
              {pagesWithFaqs.map((item) => (
                <li key={buildListingPath(item)}>
                  <button
                    type="button"
                    onClick={() =>
                      setPage({
                        department: item.department,
                        city: item.city,
                        zone: item.zone,
                        operation: item.operation,
                      })
                    }
                  >
                    {describePage(item, zoneName(item.zone))}
                  </button>
                  <span>
                    {item.count} {item.count === 1 ? "pregunta" : "preguntas"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </LayoutWeb>
  );
};

export default ListingFaqs;
